// Pronunciation drills — a port of `ui/PracticeScreen.kt`.
import Header from '../components/Header';
import SpeechNotice from '../components/SpeechNotice';
import { useStore } from '../store';
import { practicePhrases } from '../tutor/scenarios';
import type { PronunciationResult } from '../tutor/pronunciationScorer';

export default function PracticeScreen() {
  const s = useStore();
  const phrase = s.practicePhrase;
  const p = s.progress;

  return (
    <div className="screen">
      <Header
        title="Pronunciation"
        subtitle="Read the sentence out loud and get an instant score"
        stats={[
          { value: String(p.practiceAttempts), label: 'attempts' },
          { value: `${p.practiceBest}%`, label: 'best score' },
          { value: `${p.practiceAverage}%`, label: 'average' },
        ]}
      />

      <div className="scroll">
        <SpeechNotice />

        {!s.micSupported && (
          <div className="notice">
            This browser has no speech recognition, so nothing can be scored. Chrome, Edge and
            Safari support it — you can still use ▶ Listen to hear each phrase.
          </div>
        )}

        <div className="card">
          <div className="eyebrow">
            Phrase {s.practiceIndex + 1} of {practicePhrases.length} • {phrase.focus}
          </div>
          <p className="phrase">{phrase.text}</p>
          <p className="phrase-tip">{phrase.tip}</p>

          <div className="actions">
            <button type="button" className="btn outlined" onClick={s.speakPracticePhrase}>
              ▶ Listen
            </button>
            {s.micSupported && (
              <button type="button" className="btn filled" onClick={s.startPronunciationCheck}>
                {s.isListening ? '⏹ Stop' : '🎤 Read it aloud'}
              </button>
            )}
          </div>

          {s.isListening && (
            <p className="eyebrow" style={{ marginTop: 8 }}>
              Listening… speak clearly at a normal pace.
            </p>
          )}
        </div>

        {s.practiceResult && <ResultCard result={s.practiceResult} />}

        <div className="nav-row">
          <button type="button" className="btn text" onClick={s.previousPhrase}>
            ← Previous
          </button>
          <button type="button" className="btn text" onClick={s.nextPhrase}>
            Next phrase →
          </button>
        </div>

        <p className="muted">
          Scoring compares the browser's speech-to-text transcript with the target sentence. Where
          that transcription happens depends on the browser: Chrome sends the audio to Google's
          servers, while Safari and Edge can recognise on-device.
        </p>
      </div>
    </div>
  );
}

function ResultCard({ result }: { result: PronunciationResult }) {
  const tone = result.score >= 80 ? 'high' : result.score >= 60 ? 'mid' : 'low';
  return (
    <div className="card">
      <div className={`score ${tone}`}>{result.score}%</div>
      <p style={{ margin: '4px 0 0' }}>{result.feedback}</p>
      <div className="words">
        {result.words.map((word, index) => (
          <span className={`word ${word.status}`} key={`${word.expected}-${index}`}>
            {word.expected}
          </span>
        ))}
      </div>
      <p className="muted" style={{ marginTop: 12 }}>
        I heard: “{result.heard}”
      </p>
    </div>
  );
}
